import { storageSafeAuthLock } from "./authLock";
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl) {
  throw new Error("VITE_SUPABASE_URL is required");
}

if (!supabaseAnonKey) {
  throw new Error("VITE_SUPABASE_ANON_KEY is required");
}

// Safe storage adapter that falls back to memory if localStorage is unavailable
class SafeStorage {
  private memoryStorage: Map<string, string>;
  private localStorageAvailable: boolean;

  constructor() {
    this.memoryStorage = new Map<string, string>();
    this.localStorageAvailable = this.checkLocalStorageAvailable();
  }

  private checkLocalStorageAvailable(): boolean {
    try {
      // Simple check: can we access localStorage at all?
      // Don't actually write to it here - just check if it exists
      if (typeof localStorage !== 'undefined' && localStorage !== null) {
        return true;
      }
      return false;
    } catch (e) {
      console.warn('localStorage unavailable, using memory storage for auth', e);
      return false;
    }
  }

  getItem(key: string): string | null {
    if (this.localStorageAvailable) {
      try {
        const value = localStorage.getItem(key);
        // If we successfully read, confirm localStorage is working
        return value;
      } catch (e) {
        console.warn(`Failed to read ${key} from localStorage`, e);
        this.localStorageAvailable = false;
        return this.memoryStorage.get(key) || null;
      }
    }
    return this.memoryStorage.get(key) || null;
  }

  setItem(key: string, value: string): void {
    if (this.localStorageAvailable) {
      try {
        localStorage.setItem(key, value);
        // Confirm setItem worked
        return;
      } catch (e) {
        console.warn(`Failed to store ${key} in localStorage, falling back to memory:`, e);
        this.localStorageAvailable = false;
        this.memoryStorage.set(key, value);
        return;
      }
    }
    // localStorage unavailable, use memory
    this.memoryStorage.set(key, value);
  }

  removeItem(key: string): void {
    if (this.localStorageAvailable) {
      try {
        localStorage.removeItem(key);
        return;
      } catch (e) {
        console.warn(`Failed to remove ${key} from localStorage`, e);
        this.localStorageAvailable = false;
      }
    }
    this.memoryStorage.delete(key);
  }
}

export const safeStorageAdapter = new SafeStorage();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: safeStorageAdapter,
    lock: storageSafeAuthLock,
  },
  db: {
    schema: "public",
  },
  global: {
    headers: {
      "X-Client-Info": "supabase-js-v2",
    },
  },
});

// Helper function to validate session
export const validateSession = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      return null;
    }

    return session || null;
  } catch (err) {
    return null;
  }
};

export default supabase;
